import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Glass redesign — Phase 2 primitive.
 *
 * Convenience wrapper around Radix Popover (via shadcn) that
 * applies the Glass surface treatment: translucent fill at high
 * alpha, backdrop blur, soft border, and a slightly tighter
 * default padding tuned for the inline pickers used on the
 * task-detail rail and the board's filter popover.
 *
 * Two ways to use it:
 *   1. Convenience form — pass `trigger` + `children` directly,
 *      get a fully-wired popover. The shorter path; covers ~90%
 *      of use cases.
 *   2. Compositional form — import `GlassPopoverContent` alongside
 *      the existing `Popover` / `PopoverTrigger` from shadcn for
 *      cases that need explicit `<PopoverAnchor>`, `forwardRef` on
 *      the trigger, or controlled open state.
 *
 * Sizing:
 *   - `padding`: `none` (composer-style content), `sm` (the
 *      default — picker rows), `md` (multi-section panels).
 *   - `width`:   `auto`, `sm` (192), `md` (224), `lg` (288),
 *      or any explicit Tailwind utility passed via `className`.
 *
 * Anchoring:
 *   - `align`/`side`/`sideOffset` pass straight through to Radix.
 *   - Default: `align="start" sideOffset={6}` — anchored to the
 *     left edge of the trigger with a small gap; matches how the
 *     prototype's pickers feel.
 */

const paddingClasses = {
  none: 'p-0',
  sm: 'p-1.5',
  md: 'p-2.5',
} as const;

const widthClasses = {
  auto: 'w-auto',
  sm: 'w-48',
  md: 'w-56',
  lg: 'w-72',
} as const;

type PaddingKey = keyof typeof paddingClasses;
type WidthKey = keyof typeof widthClasses;

export interface GlassPopoverContentProps
  extends ComponentProps<typeof PopoverContent> {
  padding?: PaddingKey;
  width?: WidthKey;
}

/**
 * Drop-in replacement for shadcn's `<PopoverContent>` that paints
 * the Glass surface. Use when you need full control over Popover
 * composition (e.g. setting `open` controlled state on `<Popover>`).
 */
export const GlassPopoverContent = forwardRef<
  HTMLDivElement,
  GlassPopoverContentProps
>(
  (
    {
      className,
      padding = 'sm',
      width = 'auto',
      align = 'start',
      sideOffset = 6,
      ...rest
    },
    ref,
  ) => (
    <PopoverContent
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        // Override shadcn's `bg-popover` + default width so the glass
        // tokens take effect.
        'flex flex-col gap-1 rounded-lg border border-[var(--glass-popover-border)]',
        'bg-[var(--glass-popover-bg)] text-popover-foreground backdrop-blur-md',
        'shadow-[0_8px_32px_oklch(0_0_0/0.18)] ring-0',
        widthClasses[width],
        paddingClasses[padding],
        className,
      )}
      {...rest}
    />
  ),
);

GlassPopoverContent.displayName = 'GlassPopoverContent';

/**
 * Convenience form — pass `trigger` + `children`, get a fully
 * wired Glass popover. For the common case where you don't need
 * compositional control over the open state.
 */
export interface GlassPopoverProps
  extends Omit<GlassPopoverContentProps, 'children'> {
  trigger: ReactNode;
  children: ReactNode;
  /** Controlled open state. Omit for uncontrolled. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render the trigger as the child element (default true). */
  asChild?: boolean;
}

export const GlassPopover = ({
  trigger,
  children,
  open,
  onOpenChange,
  asChild = true,
  ...contentProps
}: GlassPopoverProps) => (
  <Popover open={open} onOpenChange={onOpenChange}>
    <PopoverTrigger asChild={asChild}>{trigger}</PopoverTrigger>
    <GlassPopoverContent {...contentProps}>{children}</GlassPopoverContent>
  </Popover>
);
